import { ADMIN_PORTAL_ROLES, ROLES } from '@giftsay/constants';
import * as React from 'react';
import { createBrowserRouter } from 'react-router-dom';

import { AdminShell } from '../components/layout/AdminShell.js';
import { ProtectedRoute } from '../features/auth/components/ProtectedRoute.js';
import { StaffModuleGate, StoreOwnerGate } from '../features/staff/components/StaffRouteGates.js';
import { useAppSelector } from '../store/hooks.js';
import { RouteErrorBoundary } from './ErrorBoundary.js';

const LoginPage = React.lazy(() =>
  import('../features/auth/pages/LoginPage.js').then((m) => ({ default: m.LoginPage })),
);
const DashboardPage = React.lazy(() =>
  import('../pages/DashboardPage.js').then((m) => ({ default: m.DashboardPage })),
);
const UsersListPage = React.lazy(() =>
  import('../features/users/pages/UsersListPage.js').then((m) => ({ default: m.UsersListPage })),
);
const UserDetailPage = React.lazy(() =>
  import('../features/users/pages/UserDetailPage.js').then((m) => ({ default: m.UserDetailPage })),
);
const TaxonomyBuilderPage = React.lazy(() =>
  import('../features/taxonomy/pages/TaxonomyBuilderPage.js').then((m) => ({
    default: m.TaxonomyBuilderPage,
  })),
);
const BadgesListPage = React.lazy(() =>
  import('../features/promotions/pages/BadgesListPage.js').then((m) => ({
    default: m.BadgesListPage,
  })),
);
const CouponsListPage = React.lazy(() =>
  import('../features/promotions/pages/CouponsListPage.js').then((m) => ({
    default: m.CouponsListPage,
  })),
);
const DiscountsListPage = React.lazy(() =>
  import('../features/promotions/pages/DiscountsListPage.js').then((m) => ({
    default: m.DiscountsListPage,
  })),
);
const HomepageBuilderPage = React.lazy(() =>
  import('../features/content-blocks/pages/HomepageBuilderPage.js').then((m) => ({
    default: m.HomepageBuilderPage,
  })),
);
const NavigationBuilderPage = React.lazy(() =>
  import('../features/navigation/pages/NavigationBuilderPage.js').then((m) => ({
    default: m.NavigationBuilderPage,
  })),
);
const ProductsListPage = React.lazy(() =>
  import('../features/products/pages/ProductsListPage.js').then((m) => ({
    default: m.ProductsListPage,
  })),
);
const ProductFormPage = React.lazy(() =>
  import('../features/products/pages/ProductFormPage.js').then((m) => ({
    default: m.ProductFormPage,
  })),
);
const OrdersListPage = React.lazy(() =>
  import('../features/orders/pages/OrdersListPage.js').then((m) => ({
    default: m.OrdersListPage,
  })),
);
const OrderDetailPage = React.lazy(() =>
  import('../features/orders/pages/OrderDetailPage.js').then((m) => ({
    default: m.OrderDetailPage,
  })),
);
const DeliveryZonesListPage = React.lazy(() =>
  import('../features/delivery-zones/pages/DeliveryZonesListPage.js').then((m) => ({
    default: m.DeliveryZonesListPage,
  })),
);
const StoresListPage = React.lazy(() =>
  import('../features/stores/pages/StoresListPage.js').then((m) => ({
    default: m.StoresListPage,
  })),
);
const StoreDetailPage = React.lazy(() =>
  import('../features/stores/pages/StoreDetailPage.js').then((m) => ({
    default: m.StoreDetailPage,
  })),
);
const StoreChargesPage = React.lazy(() =>
  import('../features/stores/pages/StoreChargesPage.js').then((m) => ({
    default: m.StoreChargesPage,
  })),
);
const StoreCheckoutFieldsPage = React.lazy(() =>
  import('../features/stores/pages/StoreCheckoutFieldsPage.js').then((m) => ({
    default: m.StoreCheckoutFieldsPage,
  })),
);
const StoreFeaturesPage = React.lazy(() =>
  import('../features/stores/pages/StoreFeaturesPage.js').then((m) => ({
    default: m.StoreFeaturesPage,
  })),
);
const VendorHomePage = React.lazy(() =>
  import('../features/vendor-home/pages/VendorHomePage.js').then((m) => ({
    default: m.VendorHomePage,
  })),
);
const ReviewsListPage = React.lazy(() =>
  import('../features/reviews/pages/ReviewsListPage.js').then((m) => ({
    default: m.ReviewsListPage,
  })),
);
const ReviewSettingsPage = React.lazy(() =>
  import('../features/reviews/pages/ReviewSettingsPage.js').then((m) => ({
    default: m.ReviewSettingsPage,
  })),
);
const ContactSettingsPage = React.lazy(() =>
  import('../features/settings/pages/ContactSettingsPage.js').then((m) => ({
    default: m.ContactSettingsPage,
  })),
);
const PaymentSettingsPage = React.lazy(() =>
  import('../features/settings/pages/PaymentSettingsPage.js').then((m) => ({
    default: m.PaymentSettingsPage,
  })),
);
const EmailSettingsPage = React.lazy(() =>
  import('../features/settings/pages/EmailSettingsPage.js').then((m) => ({
    default: m.EmailSettingsPage,
  })),
);
const BrandingSettingsPage = React.lazy(() =>
  import('../features/settings/pages/BrandingSettingsPage.js').then((m) => ({
    default: m.BrandingSettingsPage,
  })),
);
const ShippingSettingsPage = React.lazy(() =>
  import('../features/settings/pages/ShippingSettingsPage.js').then((m) => ({
    default: m.ShippingSettingsPage,
  })),
);
const StorePagesSettingsPage = React.lazy(() =>
  import('../features/settings/pages/StorePagesSettingsPage.js').then((m) => ({
    default: m.StorePagesSettingsPage,
  })),
);
const CustomersPage = React.lazy(() =>
  import('../features/customers/pages/CustomersPage.js').then((m) => ({
    default: m.CustomersPage,
  })),
);
const StorePaymentsPage = React.lazy(() =>
  import('../features/stores/pages/StorePaymentsPage.js').then((m) => ({
    default: m.StorePaymentsPage,
  })),
);
const ApiErrorsPage = React.lazy(() =>
  import('../features/api-errors/pages/ApiErrorsPage.js').then((m) => ({
    default: m.ApiErrorsPage,
  })),
);
const ReleasesPage = React.lazy(() =>
  import('../features/releases/pages/ReleasesPage.js').then((m) => ({
    default: m.ReleasesPage,
  })),
);
const PaymentAuditLogPage = React.lazy(() =>
  import('../features/payments/pages/PaymentAuditLogPage.js').then((m) => ({
    default: m.PaymentAuditLogPage,
  })),
);
const PaymentDiscrepanciesPage = React.lazy(() =>
  import('../features/payments/pages/PaymentDiscrepanciesPage.js').then((m) => ({
    default: m.PaymentDiscrepanciesPage,
  })),
);
const StaffPage = React.lazy(() =>
  import('../features/staff/pages/StaffPage.js').then((m) => ({ default: m.StaffPage })),
);
const NotFoundPage = React.lazy(() =>
  import('../pages/NotFoundPage.js').then((m) => ({ default: m.NotFoundPage })),
);

/** Everyone allowed into the shell: back-office staff plus vendors (who see a narrower nav and store-scoped data). */
const PORTAL_ROLES = [...ADMIN_PORTAL_ROLES, ROLES.VENDOR];

/** Pages a vendor manages for their own store — the same components staff use, scoped by the API. */
const CATALOG_MANAGER_ROLES = [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.VENDOR];

/** Orders a vendor may see/manage are already scoped to their own store server-side. */
const ORDER_MANAGER_ROLES = [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.SUPPORT_AGENT, ROLES.VENDOR];

function HomeRoute() {
  const role = useAppSelector((state) => state.auth.user?.role);
  return role === ROLES.VENDOR ? <VendorHomePage /> : <DashboardPage />;
}

function RouteLoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center text-muted-foreground">
      Loading...
    </div>
  );
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <React.Suspense fallback={<RouteLoadingFallback />}>
        <LoginPage />
      </React.Suspense>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    element: <ProtectedRoute />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        // One shell serves staff and vendors: AdminShell swaps its nav by role, and each route
        // group below narrows who may enter. A vendor bounced off a staff-only page lands on "/"
        // (their home), which this gate admits — so there's no redirect loop.
        element: <ProtectedRoute allowedRoles={PORTAL_ROLES} />,
        children: [
          {
            element: <AdminShell />,
            children: [
              { path: '/', element: <HomeRoute /> },
              {
                element: <ProtectedRoute allowedRoles={ADMIN_PORTAL_ROLES} />,
                children: [{ path: '/users', element: <UsersListPage /> }],
              },
              {
                element: <ProtectedRoute allowedRoles={[ROLES.SUPER_ADMIN]} />,
                children: [
                  { path: '/releases', element: <ReleasesPage /> },
                  { path: '/api-errors', element: <ApiErrorsPage /> },
                ],
              },
              {
                element: <ProtectedRoute allowedRoles={[ROLES.SUPER_ADMIN, ROLES.ADMIN]} />,
                children: [
                  { path: '/users/:id', element: <UserDetailPage /> },
                  { path: '/promotions/badges', element: <BadgesListPage /> },
                  { path: '/stores', element: <StoresListPage /> },
                  { path: '/stores/:id', element: <StoreDetailPage /> },
                  { path: '/stores/:id/features', element: <StoreFeaturesPage /> },
                  { path: '/stores/:id/checkout-fields', element: <StoreCheckoutFieldsPage /> },
                  { path: '/stores/:id/charges', element: <StoreChargesPage /> },
                  { path: '/stores/:id/payments', element: <StorePaymentsPage /> },
                  { path: '/payments/audit-log', element: <PaymentAuditLogPage /> },
                  { path: '/settings/payments', element: <PaymentSettingsPage /> },
                  { path: '/payment-discrepancies', element: <PaymentDiscrepanciesPage /> },
                ],
              },
              {
                element: <ProtectedRoute allowedRoles={CATALOG_MANAGER_ROLES} />,
                children: [
                  {
                    // A store's staff logins are further limited to the screens their role can view.
                    element: <StaffModuleGate />,
                    children: [
                      { path: '/taxonomy', element: <TaxonomyBuilderPage /> },
                      { path: '/homepage-builder', element: <HomepageBuilderPage /> },
                      { path: '/navigation', element: <NavigationBuilderPage /> },
                      { path: '/products', element: <ProductsListPage /> },
                      { path: '/products/:id', element: <ProductFormPage /> },
                      { path: '/promotions/coupons', element: <CouponsListPage /> },
                      { path: '/promotions/discounts', element: <DiscountsListPage /> },
                      { path: '/delivery-zones', element: <DeliveryZonesListPage /> },
                      { path: '/customers', element: <CustomersPage /> },
                      { path: '/reviews', element: <ReviewsListPage /> },
                      { path: '/reviews/settings', element: <ReviewSettingsPage /> },
                      { path: '/settings/contact', element: <ContactSettingsPage /> },
                      { path: '/settings/email', element: <EmailSettingsPage /> },
                      { path: '/settings/branding', element: <BrandingSettingsPage /> },
                      { path: '/settings/shipping', element: <ShippingSettingsPage /> },
                      { path: '/settings/pages', element: <StorePagesSettingsPage /> },
                    ],
                  },
                ],
              },
              {
                element: <ProtectedRoute allowedRoles={ORDER_MANAGER_ROLES} />,
                children: [
                  {
                    element: <StaffModuleGate />,
                    children: [
                      { path: '/orders', element: <OrdersListPage /> },
                      { path: '/orders/:id', element: <OrderDetailPage /> },
                    ],
                  },
                ],
              },
              {
                element: <StoreOwnerGate />,
                children: [{ path: '/staff', element: <StaffPage /> }],
              },
              { path: '*', element: <NotFoundPage /> },
            ],
          },
        ],
      },
    ],
  },
]);
